import type {
  MarketingSocialObservation,
  MarketingLoChannel,
  MarketingStudioData
} from '@/types/marketingStudio';

/** Same profile-only contract as marketing_instagram_handle; never infer a handle from a post URL. */
export function instagramHandle(url: string | null | undefined): string | null {
  const handle = url
    ?.trim()
    .toLowerCase()
    .match(/^https:\/\/(?:www\.)?instagram\.com\/([a-z0-9._]{1,30})\/?(?:[?#][^\s]*)?$/)?.[1];
  return handle &&
    !['p', 'reel', 'reels', 'stories', 'explore', 'accounts', 'direct', 'about'].includes(handle) &&
    !/(^\.|\.$|\.\.)/.test(handle)
    ? handle
    : null;
}

export function isSocialObservation(value: unknown): value is MarketingSocialObservation {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    ['id', 'channel_id', 'handle'].every(
      (key) => typeof row[key] === 'string' && Boolean(row[key])
    ) &&
    (row.platform === 'instagram' || row.platform === 'tiktok') &&
    typeof row.provider_user_id === 'string' &&
    /^\d+$/.test(row.provider_user_id) &&
    typeof row.followers === 'number' &&
    Number.isSafeInteger(row.followers) &&
    row.followers >= 0 &&
    typeof row.captured_at === 'string' &&
    Number.isFinite(Date.parse(row.captured_at))
  );
}

const captureDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

/** Calendar keys keep chart spacing and daily comparisons stable across DST. */
export function socialCaptureDay(timestamp: string): string {
  const parts = captureDayFormat.formatToParts(new Date(timestamp));
  return ['year', 'month', 'day']
    .map((type) => parts.find((part) => part.type === type)!.value)
    .join('-');
}

export function socialTrend(channel: MarketingLoChannel, history: MarketingSocialObservation[]) {
  const unique = new Map<string, MarketingSocialObservation>();
  for (const row of history) {
    if (
      isSocialObservation(row) &&
      row.channel_id === channel.id &&
      row.platform === channel.platform &&
      row.provider_user_id === channel.provider_user_id
    ) {
      const day = socialCaptureDay(row.captured_at);
      const previous = unique.get(day);
      // Keep raw captures in storage; display only the latest reading per Pacific day.
      if (!previous || Date.parse(row.captured_at) > Date.parse(previous.captured_at)) {
        unique.set(day, row);
      }
    }
  }
  const points = [...unique.values()].sort(
    (a, b) => Date.parse(a.captured_at) - Date.parse(b.captured_at)
  );
  const first = points[0],
    last = points.at(-1);
  const delta = points.length > 1 ? last!.followers - first.followers : null;
  return {
    points,
    delta,
    percent: delta !== null && first.followers > 0 ? (delta / first.followers) * 100 : null
  };
}

export function measuredFollowers(channel: MarketingLoChannel | undefined): number | null {
  return channel?.provider_user_id &&
    channel.followers_captured_at &&
    Number.isFinite(Date.parse(channel.followers_captured_at)) &&
    typeof channel.followers === 'number' &&
    Number.isSafeInteger(channel.followers) &&
    channel.followers >= 0
    ? channel.followers
    : null;
}

export type SocialPlatform = 'instagram' | 'tiktok';
export const SOCIAL_PLATFORMS: readonly SocialPlatform[] = ['instagram', 'tiktok'];
export const socialPlatformLabel = (platform: SocialPlatform) =>
  platform === 'instagram' ? 'Instagram' : 'TikTok';

export function socialHandle(
  platform: SocialPlatform,
  url: string | null | undefined
): string | null {
  if (platform === 'instagram') {
    return instagramHandle(url);
  }
  return tiktokProfileHandle(url);
}
function tiktokProfileHandle(url: string | null | undefined): string | null {
  const handle = url
    ?.trim()
    .toLowerCase()
    .match(/^https:\/\/(?:www\.)?tiktok\.com\/@([a-z0-9._]{2,24})\/?(?:[?#][^\s]*)?$/)?.[1];
  return handle && !handle.endsWith('.') ? handle : null;
}
export function socialProfileUrl(platform: SocialPlatform, input: string): string | null {
  const value = input.trim();
  if (!value) {
    return null;
  }
  const candidate = value.includes('://')
    ? value
    : platform === 'instagram'
      ? `https://www.instagram.com/${value.replace(/^@/, '')}/`
      : `https://www.tiktok.com/@${value.replace(/^@/, '')}`;
  const handle = socialHandle(platform, candidate);
  if (!handle) {
    throw new Error(`Enter a valid ${socialPlatformLabel(platform)} handle or profile URL.`);
  }
  return platform === 'instagram'
    ? `https://www.instagram.com/${handle}/`
    : `https://www.tiktok.com/@${handle}`;
}

export function socialAccountRows(data: MarketingStudioData, loUserId?: string) {
  const channels = new Map(
    data.loChannels.map((channel) => [
      `${channel.lo_user_id}:${channel.platform}:${channel.handle.replace(/^@/, '').toLowerCase()}`,
      channel
    ])
  );
  // Index history once; opening a large roster must not rescan every account's
  // history for every profile. socialTrend still checks platform and identity.
  const historyByChannel = new Map<string, MarketingSocialObservation[]>();
  for (const point of data.socialHistory?.observations ?? []) {
    const points = historyByChannel.get(point.channel_id) ?? [];
    points.push(point);
    historyByChannel.set(point.channel_id, points);
  }
  // LO reads intentionally omit the director roster. Resolve their own channels
  // directly without inventing participant/enrollment records.
  if (loUserId) {
    return data.loChannels
      .filter(
        (channel) =>
          channel.lo_user_id === loUserId &&
          channel.monitoring_kind !== 'company' &&
          channel.monitoring_kind !== 'unassigned' &&
          (channel.platform === 'instagram' || channel.platform === 'tiktok')
      )
      .map((channel) => {
        const followers = measuredFollowers(channel);
        return {
          person: null,
          platform: channel.platform as SocialPlatform,
          handle: channel.handle.replace(/^@/, ''),
          channel,
          followers,
          key: channel.id,
          eligible: true,
          name: 'Your account',
          stale:
            followers !== null &&
            Date.now() - Date.parse(channel.followers_captured_at!) > 48 * 60 * 60 * 1000,
          trend:
            data.socialHistory && !data.socialHistory.unavailable
              ? socialTrend(channel, historyByChannel.get(channel.id) ?? [])
              : null
        };
      });
  }
  const linkedRows = data.participants
    .flatMap((person) =>
      SOCIAL_PLATFORMS.map((platform) => {
        const handle = socialHandle(platform, person[`${platform}_url`]);
        const channel = handle
          ? channels.get(`${person.user_id}:${platform}:${handle}`)
          : undefined;
        const followers = measuredFollowers(channel);
        const stale =
          followers !== null &&
          Date.now() - Date.parse(channel!.followers_captured_at!) > 48 * 60 * 60 * 1000;
        const trend =
          channel && data.socialHistory && !data.socialHistory.unavailable
            ? socialTrend(channel, historyByChannel.get(channel.id) ?? [])
            : null;
        return {
          person,
          platform,
          handle,
          channel,
          followers,
          stale,
          trend,
          key: `${person.user_id}:${platform}`,
          eligible: person.studio_enabled && person.has_studio_permission && person.nmls_authorized,
          name:
            [person.first_name, person.last_name].filter(Boolean).join(' ') ||
            person.email ||
            'Loan officer'
        };
      })
    )
    .filter((row) => row.eligible || row.handle);
  const unassignedRows = data.loChannels
    .filter(
      (channel) =>
        channel.lo_user_id === null &&
        channel.monitoring_enabled !== false &&
        (channel.platform === 'instagram' || channel.platform === 'tiktok')
    )
    .map((channel) => {
      const followers = measuredFollowers(channel);
      return {
        person: null,
        platform: channel.platform as SocialPlatform,
        handle: channel.handle.replace(/^@/, ''),
        channel,
        followers,
        key: channel.id,
        eligible: true,
        stale:
          followers !== null &&
          Date.now() - Date.parse(channel.followers_captured_at!) > 48 * 60 * 60 * 1000,
        trend:
          data.socialHistory && !data.socialHistory.unavailable
            ? socialTrend(channel, historyByChannel.get(channel.id) ?? [])
            : null,
        name: channel.monitoring_kind === 'company' ? 'Company account' : 'LO assignment pending'
      };
    });
  return [...unassignedRows, ...linkedRows];
}

export function socialCollectionReady(data: MarketingStudioData): boolean {
  const health = data.workerHealth;
  const providers = health?.metadata?.providers as Record<string, unknown> | undefined;
  const capabilities = health?.metadata?.capabilities;
  return Boolean(
    health?.healthy &&
      health.enabled !== false &&
      providers?.apifyConfigured === true &&
      Array.isArray(capabilities) &&
      capabilities.includes('social_profiles') &&
      health.metadata?.acceptingTasks !== false
  );
}

/** The Overview window narrows measured dates, never inventing a period-start count. */
export function socialReportingRows(data: MarketingStudioData, days: number, asOf: string) {
  const end = socialCaptureDay(asOf);
  const start = new Date(Date.parse(`${end}T00:00:00Z`) - (days - 1) * 86400000)
    .toISOString()
    .slice(0, 10);
  return socialAccountRows(data).map((row) => ({
    ...row,
    trend:
      row.channel && row.trend
        ? socialTrend(
            row.channel,
            row.trend.points.filter((point) => {
              const day = socialCaptureDay(point.captured_at);
              return (
                day >= start && day <= end && Date.parse(point.captured_at) <= Date.parse(asOf)
              );
            })
          )
        : null
  }));
}
